import { createClient, RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim();
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim().replace(/\\n$/g, '');

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// ============================================
// RESILIENT SUPABASE CLIENT
// ============================================
// Addresses TrackWrestling issues:
// - Server crashes → Automatic reconnection with exponential backoff
// - Lagging feeds → Connection health monitoring
// - Page crashes → Memory leak prevention, channel management

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  realtime: {
    params: {
      eventsPerSecond: 10, // Rate limit to prevent overwhelming
    },
  },
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  global: {
    headers: {
      'x-client-info': 'tournament-platform/1.0.0',
    },
  },
});

// ============================================
// CONNECTION STATE MANAGEMENT
// ============================================

export type ConnectionStatus = 'connected' | 'connecting' | 'disconnected' | 'reconnecting';

interface ConnectionState {
  status: ConnectionStatus;
  lastConnected: Date | null;
  reconnectAttempts: number;
  latency: number | null;
}

class ConnectionManager {
  private state: ConnectionState = {
    status: 'connecting',
    lastConnected: null,
    reconnectAttempts: 0,
    latency: null,
  };
  
  private listeners: Set<(state: ConnectionState) => void> = new Set();
  private healthCheckInterval: ReturnType<typeof setInterval> | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private channels: Map<string, RealtimeChannel> = new Map();
  private isPageVisible: boolean = true;
  private initialized: boolean = false;
  
  constructor() {
    // Defer initialization to avoid blocking app startup
    // This prevents network issues from causing a blank screen on mobile
    setTimeout(() => this.initialize(), 1000);
  }
  
  private initialize() {
    if (this.initialized) return;
    this.initialized = true;
    
    this.setupVisibilityListener();
    this.setupOnlineListener();
    this.startHealthCheck();
  }

  // Track page visibility to pause/resume connections
  private setupVisibilityListener() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        this.isPageVisible = document.visibilityState === 'visible';
        
        if (this.isPageVisible) {
          // Page became visible - check connection health
          this.checkHealth();
          this.resumeChannels();
        } else {
          // Page hidden - reduce activity (but keep connection alive)
          this.pauseNonEssentialChannels();
        }
      });
    }
  }

  // Track network online/offline status
  private setupOnlineListener() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.reconnectAllChannels();
      });
      
      window.addEventListener('offline', () => {
        this.updateState({ status: 'disconnected' });
      });
    }
  }

  // Periodic health check
  private startHealthCheck() {
    // Check every 30 seconds
    this.healthCheckInterval = setInterval(() => {
      if (this.isPageVisible) {
        this.checkHealth();
      }
    }, 30000);
  }

  private async checkHealth() {
    const start = performance.now();
    
    try {
      // Simple health check query
      const { error } = await supabase
        .from('tournaments')
        .select('id')
        .limit(1)
        .maybeSingle();
      
      const latency = Math.round(performance.now() - start);
      
      if (error) {
        console.warn('[ConnectionManager] Health check failed:', error);
        this.handleDisconnect();
      } else {
        this.updateState({
          status: 'connected',
          lastConnected: new Date(),
          latency,
          reconnectAttempts: 0,
        });
      }
    } catch (err) {
      console.error('[ConnectionManager] Health check error:', err);
      this.handleDisconnect();
    }
  }

  private handleDisconnect() {
    if (this.state.status === 'reconnecting') return;
    
    this.updateState({ status: 'reconnecting' });
    this.scheduleReconnect();
  }

  private scheduleReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s
    const delay = Math.min(
      1000 * Math.pow(2, this.state.reconnectAttempts),
      30000
    );
    
    
    this.reconnectTimeout = setTimeout(() => {
      this.updateState({ reconnectAttempts: this.state.reconnectAttempts + 1 });
      this.reconnectAllChannels();
    }, delay);
  }

  private async reconnectAllChannels() {
    
    for (const [name, channel] of this.channels) {
      try {
        await supabase.removeChannel(channel);
      } catch (e) {
        // Ignore removal errors
      }
    }
    
    // Clear and let hooks re-subscribe
    this.channels.clear();
    
    // Check health after reconnecting
    setTimeout(() => this.checkHealth(), 1000);
  }

  private pauseNonEssentialChannels() {
    // Keep connections alive but reduce traffic when tab is hidden
    // Supabase handles this internally, but we can add additional logic
  }

  private resumeChannels() {
    // Resume full activity when tab becomes visible
    this.checkHealth();
  }

  private updateState(partial: Partial<ConnectionState>) {
    this.state = { ...this.state, ...partial };
    this.notifyListeners();
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.state));
  }

  // Public API
  subscribe(listener: (state: ConnectionState) => void): () => void {
    this.listeners.add(listener);
    // Immediately notify with current state
    listener(this.state);
    
    return () => {
      this.listeners.delete(listener);
    };
  }

  getState(): ConnectionState {
    return { ...this.state };
  }

  registerChannel(name: string, channel: RealtimeChannel) {
    this.channels.set(name, channel);
  }

  unregisterChannel(name: string) {
    this.channels.delete(name);
  }

  // Force reconnect (can be called by user action)
  forceReconnect() {
    this.updateState({ reconnectAttempts: 0 });
    this.reconnectAllChannels();
  }

  destroy() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    this.listeners.clear();
    this.channels.clear();
  }
}

// Singleton instance
export const connectionManager = new ConnectionManager();

// ============================================
// RESILIENT CHANNEL CREATION
// ============================================

interface ResilientChannelOptions {
  name: string;
  onStatusChange?: (status: string) => void;
  onError?: (error: Error) => void;
}

export function createResilientChannel(
  options: ResilientChannelOptions
): RealtimeChannel {
  const channel = supabase.channel(options.name, {
    config: {
      broadcast: { self: true },
      presence: { key: '' },
    },
  });

  // Track channel status
  channel.on('system', { event: '*' }, (payload) => {
  });

  // Register with connection manager
  connectionManager.registerChannel(options.name, channel);

  return channel;
}

// ============================================
// STALE DATA DETECTION
// ============================================

export interface StaleDataConfig {
  maxAge: number; // milliseconds
  onStale?: () => void;
}

export function isDataStale(lastUpdated: Date | null, maxAge: number): boolean {
  if (!lastUpdated) return true;
  return Date.now() - lastUpdated.getTime() > maxAge;
}

// ============================================
// REQUEST DEDUPLICATION
// ============================================

const pendingRequests = new Map<string, Promise<any>>();

export async function deduplicatedQuery<T>(
  key: string,
  queryFn: () => Promise<T>,
  ttlMs: number = 100 // Deduplicate requests within 100ms
): Promise<T> {
  const existing = pendingRequests.get(key);
  if (existing) {
    return existing;
  }

  const promise = queryFn().finally(() => {
    setTimeout(() => pendingRequests.delete(key), ttlMs);
  });

  pendingRequests.set(key, promise);
  return promise;
}

// ============================================
// BATCH OPERATIONS
// ============================================

interface BatchOperation<T> {
  key: string;
  resolve: (value: T) => void;
  reject: (error: Error) => void;
}

class BatchProcessor<T> {
  private queue: BatchOperation<T>[] = [];
  private timeout: ReturnType<typeof setTimeout> | null = null;
  private batchSize: number;
  private delayMs: number;
  private processor: (keys: string[]) => Promise<Map<string, T>>;

  constructor(
    processor: (keys: string[]) => Promise<Map<string, T>>,
    batchSize: number = 50,
    delayMs: number = 10
  ) {
    this.processor = processor;
    this.batchSize = batchSize;
    this.delayMs = delayMs;
  }

  async load(key: string): Promise<T> {
    return new Promise((resolve, reject) => {
      this.queue.push({ key, resolve, reject });
      
      if (this.queue.length >= this.batchSize) {
        this.flush();
      } else if (!this.timeout) {
        this.timeout = setTimeout(() => this.flush(), this.delayMs);
      }
    });
  }

  private async flush() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    const batch = this.queue.splice(0, this.batchSize);
    if (batch.length === 0) return;

    const keys = [...new Set(batch.map(op => op.key))];

    try {
      const results = await this.processor(keys);
      
      batch.forEach(op => {
        const result = results.get(op.key);
        if (result !== undefined) {
          op.resolve(result);
        } else {
          op.reject(new Error(`No result for key: ${op.key}`));
        }
      });
    } catch (error) {
      batch.forEach(op => op.reject(error as Error));
    }
  }
}

// Participant batch loader
export const participantLoader = new BatchProcessor<any>(
  async (ids) => {
    const { data, error } = await supabase
      .from('participants')
      .select('*')
      .in('id', ids);
    
    if (error) throw error;
    
    const map = new Map<string, any>();
    (data || []).forEach(p => map.set(p.id, p));
    return map;
  }
);

// Team batch loader
export const teamLoader = new BatchProcessor<any>(
  async (ids) => {
    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .in('id', ids);
    
    if (error) throw error;
    
    const map = new Map<string, any>();
    (data || []).forEach(t => map.set(t.id, t));
    return map;
  }
);
